import React from "react";
import { useTranslation } from "react-i18next";
import Drawer from "@/components/ui/drawer";
import Button from "@/components/ui/button";
import Chip from "@/components/ui/chip";
import {
  Eye as EyeIcon,
  Calendar as CalendarIcon,
  Clock as ClockIcon,
  Location as LocationIcon,
  Category as CategoryIcon,
  Finance as FinanceIcon,
  Employees as EmployeesIcon,
} from "@/components/icons";
import { formatDate, formatDateLong } from "@/utils";
import type {
  EducationStatus,
  EducationFormat,
  EducationDetailApi,
} from "../types/education-management.types";
import {
  apiTypeToFormat,
  apiStatusToStatus,
} from "../services/education-management.api";
import { formatDurationFromParts } from "@/utils/format-duration";
import styles from "./EducationDetailDrawer.module.scss";

interface EducationDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  educationDetail: EducationDetailApi | null;
  isLoading?: boolean;
}

// Status configurations
const STATUS_TYPES: Record<
  EducationStatus,
  "success" | "warning" | "error" | "info" | "default"
> = {
  aktif: "success",
  talepToplama: "warning",
  kapali: "default",
  tamamlandi: "info",
  butceBekliyor: "warning",
  taslak: "error",
};

const FORMAT_TYPES: Record<
  EducationFormat,
  "warning" | "info" | "success"
> = {
  uniteli: "info",
  canli: "success",
  yuzYuze: "warning",
  nonUnit: "info",
};

/** Eğitmen adı: ad soyad varsa kullan, yoksa "Adı girilmemiş" */
function getEducatorDisplayName(
  instructors: EducationDetailApi["instructors"],
  fallback: string,
): string {
  if (!instructors?.length) return "";
  return instructors
    .map((i) => {
      const name = [i.firstName, i.lastName].filter(Boolean).join(" ").trim();
      return name || fallback;
    })
    .join(", ");
}

/** scheduledTimes'ı okunabilir metne çevir */
function formatScheduledTimes(
  scheduledTimes: EducationDetailApi["scheduledTimes"] | undefined,
  t: (k: string) => string,
): string {
  if (!scheduledTimes?.length) return "";
  const dayKeys: Record<number, string> = {
    0: "sunday",
    1: "monday",
    2: "tuesday",
    3: "wednesday",
    4: "thursday",
    5: "friday",
    6: "saturday",
  };
  return scheduledTimes
    .map(
      (s) =>
        `${dayKeys[s.day] ? t(`dayLabels.${dayKeys[s.day]}`) : `${s.day}`} ${s.startTime} - ${s.endTime}${s.timezone ? ` (${s.timezone})` : ""}`,
    )
    .join(", ");
}

const EducationDetailDrawer: React.FC<EducationDetailDrawerProps> = ({
  isOpen,
  onClose,
  educationDetail,
  isLoading = false,
}) => {
  const { t } = useTranslation();

  const format = educationDetail
    ? apiTypeToFormat(educationDetail.type)
    : "uniteli";
  const status = educationDetail
    ? apiStatusToStatus(educationDetail.status)
    : "aktif";
  // Sadece canlı (online) aktif eğitimler "Sanal Sınıf" olarak işaretlenir;
  // üniteli eğitimlerde sınıf kavramı yok.
  const isVirtualClassroom = status === "aktif" && format === "canli";
  const statusConfig = isVirtualClassroom
    ? { label: t("educationManagement.virtualClassroom"), type: "info" as const }
    : { label: t(`educationManagement.statusOptions.${status}`), type: STATUS_TYPES[status] };
  const formatConfig = {
    label: t(`educationManagement.formatOptions.${format}`),
    type: FORMAT_TYPES[format],
  };
  const audienceMap: Record<number, string> = {
    0: t("createEducation.basicInfo.targetAudienceOptions.central"),
    1: t("createEducation.basicInfo.targetAudienceOptions.branch"),
    2: t("createEducation.basicInfo.targetAudienceOptions.all"),
  };
  const targetAudienceLabel = educationDetail
    ? audienceMap[educationDetail.scopeType] ?? ""
    : "";

  const datePendingLabel = t("common.datePending");
  // formatDateLong zaten saat göstermiyor ("12 Şubat 2026"). Eski fallback
  // `|| educationDetail.createdAt` ISO string'i (2026-04-22T00:00:00.000Z gibi)
  // olduğu gibi gösteriyordu — kullanıcıya "…T00:00:00" karmaşası olarak
  // görünüyordu. Fallback'ı "—" yaptık.
  const creationDate = educationDetail?.createdAt
    ? formatDateLong(educationDetail.createdAt) || "—"
    : "—";
  const educationDateTime = educationDetail
    ? educationDetail.startDate && educationDetail.endDate
      ? `${formatDate(educationDetail.startDate, "DD.MM.YYYY HH:mm")} - ${formatDate(educationDetail.endDate, "DD.MM.YYYY HH:mm")}`
      : (() => {
          const scheduled = formatScheduledTimes(
            educationDetail.scheduledTimes,
            t,
          );
          if (scheduled) return scheduled;
          if (educationDetail.startDate)
            return formatDate(educationDetail.startDate, "DD.MM.YYYY HH:mm");
          return datePendingLabel;
        })()
    : datePendingLabel;
  const location = educationDetail?.location?.trim()
    ? educationDetail.location
    : "—";
  const transportation = educationDetail?.transportation?.trim()
    ? educationDetail.transportation
    : "—";
  const categories = educationDetail?.categories ?? [];
  const educators = educationDetail
    ? getEducatorDisplayName(
        educationDetail.instructors,
        t("educationManagement.detail.instructorNameNotSet"),
      ) || "—"
    : "—";
  const requestCollectionDateRange =
    educationDetail?.requestCollectionStartDate &&
    educationDetail?.requestCollectionEndDate
      ? `${formatDate(educationDetail.requestCollectionStartDate, "DD.MM.YYYY HH:mm")} - ${formatDate(educationDetail.requestCollectionEndDate, "DD.MM.YYYY HH:mm")}`
      : educationDetail?.isAcceptingRequestCollection
        ? datePendingLabel
        : "—";
  const budget =
    educationDetail != null
      ? new Intl.NumberFormat("tr-TR", {
          style: "currency",
          currency: "TRY",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(educationDetail.budget)
      : "—";
  // Liste sayfası `totalEnrolledCount` kullanıyor; detay drawer
  // `requestedEnrollmentsCount` kullandığında bazı eğitimlerde 0 görünüp
  // listedeki "1/500" değeriyle çelişiyordu. Aynı alandan okuyup tutarlı
  // tutuyoruz.
  const demandCount =
    educationDetail != null
      ? `${
          educationDetail.totalEnrolledCount ??
          educationDetail.requestedEnrollmentsCount ??
          0
        } / ${educationDetail.capacity}`
      : "—";
  const description = educationDetail?.description?.trim() ?? "—";
  const content = educationDetail?.content?.trim() ?? "—";
  const educationCode = educationDetail?.code?.trim() || "—";
  const durationLabel = educationDetail
    ? (() => {
        const formatted = formatDurationFromParts(
          educationDetail.durationHours,
          educationDetail.durationMinutes,
        );
        return formatted === "-" ? "—" : formatted;
      })()
    : "—";

  const examDisplay =
    educationDetail != null && educationDetail.exam != null
      ? (() => {
          const exam = educationDetail.exam as
            | string
            | { id?: string; name?: string; title?: string };
          if (typeof exam === "string") return exam;
          if (exam.name && exam.name.trim().length > 0) return exam.name;
          if (exam.title && exam.title.trim().length > 0) return exam.title;
          if (exam.id && exam.id.trim().length > 0) return exam.id;
          return "—";
        })()
      : "—";

  const jitsiUrl = (() => {
    for (const ch of educationDetail?.chapters ?? []) {
      const obj = ch.objectives?.find(
        (o) =>
          o.externalUrl?.trim() &&
          (o.type === 2 || o.externalUrl.toLowerCase().includes("meet.jit.si")),
      );
      const baseUrl = obj?.externalUrl?.trim();
      if (!baseUrl) continue;
      // JWT'yi URL'e ekle ki self-hosted prosody odaya kabul etsin (moderator flag dahil).
      const token = (obj as { token?: string } | undefined)?.token?.trim();
      if (!token) return baseUrl;
      const sep = baseUrl.includes("?") ? "&" : "?";
      return `${baseUrl}${sep}jwt=${encodeURIComponent(token)}`;
    }
    return null;
  })();

  const headerTitle = (
    <span className={styles.headerTitle}>
      <EyeIcon width={24} height={24} color="#14181F" />
      {t("educationManagement.detail.title")}
    </span>
  );

  const headerActions = jitsiUrl ? (
    <Button
      variant="outlined"
      color="primary"
      size="medium"
      className={styles.joinButton}
      onClick={() => window.open(jitsiUrl, "_blank", "noopener,noreferrer")}
    >
      {t("educationManagement.detail.joinEducation")}
    </Button>
  ) : undefined;

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        title={headerTitle}
        titleBadge={educationDetail?.name ?? undefined}
        headerActions={headerActions}
        width="561px"
        position="right"
      >
        <div className={styles.drawerContent}>
          {isLoading ? (
            <div className={styles.loadingState}>
              <span>{t("common.loading")}</span>
            </div>
          ) : !educationDetail ? (
            <div className={styles.emptyState}>
              <span>
                {t("educationManagement.detail.notFound")}
              </span>
            </div>
          ) : (
            <>
              {/* Cover image */}
              {educationDetail.coverImageUrl ? (
                <div className={styles.imageContainer}>
                  <img
                    src={educationDetail.coverImageUrl}
                    alt={educationDetail.name}
                    className={styles.educationImage}
                  />
                </div>
              ) : educationDetail.coverImageFileID ? (
                <div className={styles.imageContainer}>
                  <img
                    src={(() => {
                      const base = (import.meta.env.VITE_API_URL || "").replace(
                        /\/$/,
                        "",
                      );
                      return base
                        ? `${base}/api/files/${educationDetail.coverImageFileID}`
                        : `/api/files/${educationDetail.coverImageFileID}`;
                    })()}
                    alt={educationDetail.name}
                    className={styles.educationImage}
                  />
                </div>
              ) : null}

              {/* Hero banner */}
              <div className={styles.hero}>
                <h2 className={styles.heroTitle}>{educationDetail.name}</h2>
                <div className={styles.heroChips}>
                  <Chip type={formatConfig.type} size="small">
                    {formatConfig.label}
                  </Chip>
                  <Chip type={statusConfig.type} size="small">
                    {statusConfig.label}
                  </Chip>
                  {categories.slice(0, 2).map((cat) => (
                    <Chip
                      key={cat.id}
                      type="default"
                      size="small"
                      variant="outlined"
                    >
                      {cat.name}
                    </Chip>
                  ))}
                  {categories.length > 2 && (
                    <Chip type="default" size="small" variant="outlined">
                      +{categories.length - 2}
                    </Chip>
                  )}
                </div>

                {/* Demand progress */}
                {educationDetail.capacity > 0 && (
                  <div className={styles.demandBlock}>
                    <div className={styles.demandHeader}>
                      <span className={styles.demandLabelWrap}>
                        <EmployeesIcon width={14} height={14} />
                        Talep
                      </span>
                      <span className={styles.demandCount}>{demandCount}</span>
                    </div>
                    <div className={styles.demandBar}>
                      <div
                        className={styles.demandBarFill}
                        style={{
                          width: `${Math.min(
                            100,
                            Math.round(
                              ((educationDetail.totalEnrolledCount ??
                                educationDetail.requestedEnrollmentsCount ??
                                0) /
                                educationDetail.capacity) *
                                100,
                            ),
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* 1) Genel Bilgi */}
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <CategoryIcon width={16} height={16} />
                  <span>Genel Bilgi</span>
                </div>
                <div className={styles.sectionGrid}>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>{t("educationManagement.detail.code")}</span>
                    <span className={styles.fieldValue}>
                      {educationCode !== "—" ? (
                        educationCode
                      ) : (
                        <span className={styles.empty}>{t("educationManagement.detail.notSpecified")}</span>
                      )}
                    </span>
                  </div>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>{t("common.createdAt")}</span>
                    <span className={styles.fieldValue}>{creationDate}</span>
                  </div>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Hedef Kitle</span>
                    <span className={styles.fieldValue}>
                      {targetAudienceLabel || (
                        <span className={styles.empty}>—</span>
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* 2) Tarih & Süre */}
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <CalendarIcon width={16} height={16} />
                  <span>{t("educationManagement.detail.dateAndTime")}</span>
                </div>
                <div className={styles.sectionList}>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>{t("educationManagement.detail.trainingDate")}</span>
                    <span className={styles.fieldValue}>
                      {educationDateTime}
                    </span>
                  </div>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>Talep Toplama</span>
                    <span className={styles.fieldValue}>
                      {requestCollectionDateRange}
                    </span>
                  </div>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>{t("createEducation.sidebar.duration")}</span>
                    <span className={styles.fieldValue}>
                      {durationLabel !== "—" ? (
                        <span className={styles.durationBadge}>
                          <ClockIcon width={14} height={14} /> {durationLabel}
                        </span>
                      ) : (
                        <span className={styles.empty}>{t("educationManagement.detail.notSpecified")}</span>
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* 3) Konum */}
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <LocationIcon width={16} height={16} />
                  <span>Konum & Lojistik</span>
                </div>
                <div className={styles.sectionList}>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>Lokasyon</span>
                    <span className={styles.fieldValue}>
                      {location !== "—" ? (
                        location
                      ) : (
                        <span className={styles.empty}>
                          {format === "canli"
                            ? t("educationManagement.detail.onlineNoLocation")
                            : t("educationManagement.detail.notSpecified")}
                        </span>
                      )}
                    </span>
                  </div>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>{t("createEducation.location.transportation")}</span>
                    <span className={styles.fieldValue}>
                      {transportation !== "—" ? (
                        transportation
                      ) : (
                        <span className={styles.empty}>{t("educationManagement.detail.notSpecified")}</span>
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* 4) Bütçe, Sınav, Eğitmen */}
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <FinanceIcon width={16} height={16} />
                  <span>{t("educationManagement.detail.budgetExamInstructor")}</span>
                </div>
                <div className={styles.sectionGrid}>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>{t("createEducation.sidebar.budgetRequirement")}</span>
                    <span className={styles.fieldValueLarge}>{budget}</span>
                  </div>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>{t("examSection.heading")}</span>
                    <span className={styles.fieldValue}>
                      {examDisplay !== "—" ? (
                        examDisplay
                      ) : (
                        <span className={styles.empty}>{t("educationManagement.detail.noLinkedExam")}</span>
                      )}
                    </span>
                  </div>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>{t("createEducation.sidebar.instructor")}</span>
                    <span className={styles.fieldValue}>
                      {educators !== "—" ? (
                        educators
                      ) : (
                        <span className={styles.empty}>{t("educationManagement.detail.notAssigned")}</span>
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Content sections */}
              <div className={styles.contentCard}>
                <h4 className={styles.contentTitle}>
                  {t("educationManagement.detail.description")}
                </h4>
                <p className={styles.contentText}>
                  {description !== "—" ? (
                    description
                  ) : (
                    <span className={styles.empty}>{t("educationManagement.detail.noDescription")}</span>
                  )}
                </p>
              </div>
              <div className={styles.contentCard}>
                <h4 className={styles.contentTitle}>
                  {t("educationManagement.detail.content")}
                </h4>
                <p className={styles.contentText}>
                  {content !== "—" ? (
                    content
                  ) : (
                    <span className={styles.empty}>{t("educationManagement.detail.noContent")}</span>
                  )}
                </p>
              </div>
            </>
          )}
        </div>
      </Drawer>
    </>
  );
};

export default EducationDetailDrawer;
